import React, {useState, useMemo} from "react";
import {
	Box,
	Typography,
	Paper,
	useTheme,
	Skeleton,
	ButtonBase
} from "@mui/material";
import {alpha} from "@mui/material";
import {makeStyles} from "tss-react/mui";
import {useSSTranslation} from "@/i18n/useSSTranslation";
import PauseStreakDrawer from "./PauseStreakDrawer";
import PauseStreakDrawerContent from "./PauseStreakDrawerContent";
import {useGetStreakInfo, useGetMe} from "@/app2/shared/data";
import {getDateInTimezone} from "@/app2/shared/utils/dateManipulation";
import {useDevCycleSdk} from "@/app2/shared/hooks";
import {LEADERBOARDS_CARD_BORDER} from "@/constants";

const useLeaderboardsStreakStyles = makeStyles()((theme) => ({
	buttonWrap: {
		width: "100%",
		height: "100%",
		display: "block",
		borderRadius: theme.spacing(3),
		textAlign: "center",
		[theme.breakpoints.up("sm")]: {
			textAlign: "left"
		},
		"&:hover:not(.Mui-disabled) > .MuiPaper-root, &:focus-visible:not(.Mui-disabled) > .MuiPaper-root": {
			border: `${theme.spacing(0.125)} solid ${theme.palette.primary.main}`
		}
	},
	paper: {
		display: "flex",
		flexDirection: "column",
		alignItems: "stretch",
		color: theme.palette.text.primary,
		padding: `${theme.spacing(3.5)} ${theme.spacing(3)}`,
		borderRadius: theme.spacing(3),
		backgroundColor: theme.palette.background.paperElevation1,
		border: `${theme.spacing(0.125)} solid ${LEADERBOARDS_CARD_BORDER}`,
		width: "100%",
		height: "100%",
		boxSizing: "border-box",
		[theme.breakpoints.up("sm")]: {
			padding: `${theme.spacing(2.5)} ${theme.spacing(3)}`
		},
		[theme.breakpoints.down("sm")]: {
			padding: `${theme.spacing(2.5)} ${theme.spacing(2)}`
		}
	},
	paperPaused: {
		backgroundColor: alpha(theme.palette.background.paperElevation1, 0.65)
	},
	pausedLabel: {
		width: "100%",
		textAlign: "center",
		marginBottom: theme.spacing(1.5),
		color: theme.palette.text.primary,
		fontWeight: theme.typography.fontWeightBold,
		opacity: 0.85,
		letterSpacing: "0.06em",
		[theme.breakpoints.up("sm")]: {
			textAlign: "left"
		}
	},
	pairRow: {
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		flex: 1,
		width: "100%",
		minHeight: 0,
		minWidth: 0,
		gap: theme.spacing(2),
		[theme.breakpoints.up("sm")]: {
			flexDirection: "row",
			alignItems: "center",
			justifyContent: "space-between",
			gap: theme.spacing(2)
		}
	},
	visualWrap: {
		display: "flex",
		justifyContent: "center",
		flexShrink: 0,
		maxWidth: "100%",
		[theme.breakpoints.up("sm")]: {
			justifyContent: "flex-start",
			alignSelf: "center"
		},
		[theme.breakpoints.down("xs")]: {
			transform: "scale(0.9)",
			transformOrigin: "center center"
		}
	},
	contentColumn: {
		display: "flex",
		flexDirection: "column",
		alignItems: "stretch",
		width: "100%",
		minWidth: 0,
		[theme.breakpoints.up("sm")]: {
			alignItems: "flex-start",
			flex: 1,
			width: "auto"
		}
	},
	streakNumbersRow: {
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		gap: theme.spacing(0.5),
		marginBottom: theme.spacing(1.5),
		justifyContent: "center",
		width: "100%",
		[theme.breakpoints.up("sm")]: {
			flexDirection: "row",
			alignItems: "center",
			justifyContent: "flex-start",
			marginBottom: theme.spacing(1),
			marginTop: 0
		}
	},
	streakBody: {
		lineHeight: 1.45,
		maxWidth: theme.spacing(35),
		textAlign: "center",
		width: "100%",
		wordBreak: "break-word",
		overflowWrap: "anywhere",
		[theme.breakpoints.up("sm")]: {
			textAlign: "left",
			maxWidth: "100%",
			paddingRight: theme.spacing(1)
		}
	},
	/** Matches `TodayLearningCard` module title (`variant="h4"` + `title` class). */
	streakTitle: {
		marginBottom: 0,
		overflow: "hidden",
		textOverflow: "ellipsis",
		display: "-webkit-box",
		WebkitLineClamp: "2",
		WebkitBoxOrient: "vertical"
	}
}));

const StreakCard = () => {
	const {t} = useSSTranslation();
	const theme = useTheme();
	const {classes: lbClasses, cx} = useLeaderboardsStreakStyles();
	const {leaderboards: isLeaderboardsCard} = useDevCycleSdk();
	const {data: streakInfo, isLoading: isLoadingStreak} = useGetStreakInfo();
	const {data: me, isPending: isMePending} = useGetMe();
	const [openDrawer, setOpenDrawer] = useState(false);

	// Extract streak data
	const streak = streakInfo?.streak ?? 0;
	const isPaused = streakInfo?.isStreakPaused ?? false;
	const isPauseStreakEnabled = streakInfo?.isPauseStreakEnabled ?? false;
	const isTodayTimeOff = streakInfo?.isTodayTimeOff ?? false;

	const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
	const userTimezone = me?.timezone ?? browserTz;

	/** Aligns with get-me / Today Learning: completed a lesson today in the learner's timezone. */
	const hasCompleteLessonsToday = useMemo(() => {
		const completedAt = me?.latestLessonCompletedAt;
		if (!completedAt) return false;
		const todayStr = getDateInTimezone(new Date(), userTimezone);
		const completedDateStr = getDateInTimezone(new Date(completedAt), userTimezone);
		return todayStr === completedDateStr;
	}, [me?.latestLessonCompletedAt, userTimezone]);

	const streakLabel =
		streak === 1 ? t("streak_day", "day streak") : t("streak_days", "day streak");

	const streakBodyText = isTodayTimeOff
		? t(
			"streak_day_off",
			"No lessons scheduled for today. Enjoy your day off!"
		)
		: hasCompleteLessonsToday
			? t(
				"streak_lesson_completed",
				"Today's lesson completed! Come back tomorrow to continue your streak."
			)
			: t("streak_day_message", "Complete one lesson to increase your streak.");

	const handleClick = () => {
		setOpenDrawer(true);
	};

	const handleCloseDrawer = (dataFromDrawer?: any) => {
		setOpenDrawer(false);
	};

	if (isLoadingStreak || streakInfo === undefined || isMePending) {
		if (isLeaderboardsCard) {
			return (
				<Paper
					elevation={0}
					sx={(theme) => ({
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						justifyContent: "space-between",
						gap: theme.spacing(2),
						color: "white",
						px: {xs: 2, sm: 3},
						py: {xs: 2.5, sm: 3.5},
						borderRadius: theme.spacing(3),
						backgroundColor: theme.palette.background.paperElevation1,
						border: `${theme.spacing(0.125)} solid ${LEADERBOARDS_CARD_BORDER}`,
						width: "100%",
						height: "100%",
						boxSizing: "border-box",
						[theme.breakpoints.up("sm")]: {
							flexDirection: "row",
							alignItems: "center"
						}
					})}
				>
					<Box sx={{flexShrink: 0}}>
						<Skeleton
							variant="rounded"
							width={112}
							height={108}
							sx={(theme) => ({
								borderRadius: 2,
								bgcolor: "rgba(255,255,255,0.08)",
								[theme.breakpoints.down("xs")]: {
									width: 96,
									height: 92
								}
							})}
						/>
					</Box>
					<Box sx={{flex: 1, width: "100%", minWidth: 0}}>
						<Skeleton variant="text" width="70%" height={36} sx={{mb: 1}} />
						<Skeleton variant="text" width="85%" height={22} />
						<Skeleton variant="text" width="75%" height={22} />
					</Box>
				</Paper>
			);
		}

		return (
			<Paper
				elevation={3}
				sx={{
					display: "flex",
					alignItems: "center",
					color: "white",
					p: "8px",
					borderRadius: "16px",
					backgroundColor: "#282828",
					border: "solid 1px #A1A1A026",
					position: "relative",
					width: {xs: "100%", lg: "51%", xl: "51%"}
				}}
			>
				<Box sx={{width: "93px", height: "89px"}}>
					<Skeleton variant="circular" width={"89px"} height={"89px"} />
				</Box>
				<Box sx={{ml: 2, flex: 1}}>
					<Skeleton variant="text" width="60%" height={32} />
					<Skeleton variant="text" width="90%" height={24} />
					<Skeleton variant="text" width="80%" height={24} />
				</Box>
			</Paper>
		);
	}

	return (
		<>
			<ButtonBase
				onClick={handleClick}
				disabled={!isPauseStreakEnabled}
				className={isLeaderboardsCard ? lbClasses.buttonWrap : undefined}
				sx={
					isLeaderboardsCard
						? undefined
						: {
							width: {xs: "100%", lg: "51%", xl: "51%"},
							height: "auto",
							borderRadius: "16px",
							display: "block",
							textAlign: "left"
						}
				}
				TouchRippleProps={{
					style: {color: theme.palette.primary.main}
				}}
			>
				{isLeaderboardsCard ? (
					<Paper
						elevation={0}
						className={cx(lbClasses.paper, isPaused && lbClasses.paperPaused)}
					>
						{isPaused && (
							<Typography variant="h6" className={lbClasses.pausedLabel}>
								{t("common_paused", "paused").toUpperCase()}
							</Typography>
						)}
						<Box className={lbClasses.pairRow}>
							<Box className={lbClasses.visualWrap}>
								<Box sx={{opacity: isPaused ? 0.4 : 1}}>
									<PauseStreakDrawerContent
										streakDays={streak}
										width="112px"
										height="108px"
									/>
								</Box>
							</Box>
							<Box className={lbClasses.contentColumn}>
								<Box className={lbClasses.streakNumbersRow}>
									<Typography
										variant="h4"
										component="div"
										className={lbClasses.streakTitle}
										sx={{
											color: "#ffffff",
											opacity: isPaused ? 0.4 : 1,
											textAlign: "inherit"
										}}
									>
										<Box component="span" sx={{color: theme.palette.primary.main}}>
											{streak}
										</Box>{" "}
										<Box component="span" sx={{color: "#ffffff"}}>
											{streakLabel}
										</Box>
									</Typography>
								</Box>
								<Typography
									variant="body2"
									color="text.secondary"
									className={lbClasses.streakBody}
									sx={{
										opacity: isPaused ? 0.2 : 1
									}}
								>
									{streakBodyText}
								</Typography>
							</Box>
						</Box>
					</Paper>
				) : (
					<Paper
						elevation={3}
						sx={{
							display: "flex",
							alignItems: "center",
							color: "white",
							p: "8px",
							borderRadius: "16px",
							backgroundColor: isPaused ? "rgba(40,40,40,0.2)" : "#282828",
							border: "solid 1px",
							borderColor: streak > 99 ? theme.palette.primary.main : "#A1A1A026",
							boxShadow:
								streak > 99
									? "65px 67px 26px rgba(255,158,0,0), 20px 20px 24px rgba(255,158,0,0.01), 8px 8px 20px rgba(255,158,0,0.02), 4px 4px 15px rgba(255,158,0,0.04), 2px 2px 1px rgba(255,158,0,0.07)"
									: "none",
							width: "100%"
						}}
					>
						<Box sx={{opacity: isPaused ? 0.4 : 1}}>
							<PauseStreakDrawerContent streakDays={streak} width="93px" height="89px" />
						</Box>

						<Box width="100%">
							<Box
								display="flex"
								alignItems="center"
								gap={2}
								mb={1}
								justifyContent="space-between"
								width="100%"
							>
								<Typography
									variant="h5"
									component="div"
									sx={{
										color: theme.palette.text.primary,
										opacity: isPaused ? 0.4 : 1,
										fontWeight: 700,
										lineHeight: 1.2
									}}
								>
									<Box component="span" sx={{color: theme.palette.primary.main}}>
										{streak}
									</Box>{" "}
									{streakLabel}
								</Typography>
								{isPaused && (
									<Typography
										variant="h6"
										sx={{
											color: theme.palette.text.primary,
											fontWeight: "600",
											opacity: 0.7
										}}
									>
										{t("common_paused", "paused").toUpperCase()}
									</Typography>
								)}
							</Box>

							<Typography
								variant="body2"
								sx={{
									color: "#FFFFFFB2",
									opacity: isPaused ? 0.2 : 1,
									lineHeight: 1.45
								}}
							>
								{streakBodyText}
							</Typography>
						</Box>
					</Paper>
				)}
			</ButtonBase>
			<PauseStreakDrawer
				open={openDrawer}
				onClose={handleCloseDrawer}
				streakDays={streak}
				isPaused={isPaused}
			/>
		</>
	);
};

export default StreakCard;
